import { useEffect, useState } from "react";
import api from "../lib/api";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line } from "recharts";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Analytics() {
  const [kpis, setKpis] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [filterAcc, setFilterAcc] = useState("all");
  const [analysis, setAnalysis] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const [k, a] = await Promise.all([api.listKpis(), api.listAccounts()]);
      setKpis(k); setAccounts(a);
    })();
  }, []);

  const filtered = filterAcc === "all" ? kpis : kpis.filter(k => k.account_id === filterAcc);

  const byDate = {};
  filtered.forEach(k => {
    if (!byDate[k.date]) byDate[k.date] = { date: k.date, reach: 0, engagement_rate: 0, watchtime_pct: 0, comments: 0, shares: 0, saves: 0, count: 0 };
    const e = byDate[k.date];
    e.reach += k.reach; e.engagement_rate += k.engagement_rate; e.watchtime_pct += k.watchtime_pct;
    e.comments += k.comments; e.shares += k.shares; e.saves += k.saves; e.count += 1;
  });
  const series = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date)).map(d => ({
    ...d, engagement_rate: +(d.engagement_rate / d.count).toFixed(2), watchtime_pct: +(d.watchtime_pct / d.count).toFixed(1)
  }));

  const analyze = async () => {
    setLoading(true); setAnalysis("");
    try {
      const summary = `Période 14j. Reach total: ${filtered.reduce((s,k)=>s+k.reach,0)}. Engagement moyen: ${(filtered.reduce((s,k)=>s+k.engagement_rate,0)/filtered.length).toFixed(2)}%. Watchtime moyen: ${(filtered.reduce((s,k)=>s+k.watchtime_pct,0)/filtered.length).toFixed(1)}%. Commentaires: ${filtered.reduce((s,k)=>s+k.comments,0)}. Shares: ${filtered.reduce((s,k)=>s+k.shares,0)}. Saves: ${filtered.reduce((s,k)=>s+k.saves,0)}. Nouveaux abonnés: ${filtered.reduce((s,k)=>s+k.new_followers,0)}.`;
      const r = await api.aiAnalyze({ prompt: summary });
      setAnalysis(r.analysis || "");
    } catch { toast.error("Erreur IA"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-8 animate-fadeUp" data-testid="analytics-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div className="border-l-2 border-[#CCFF00] pl-5">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-2">// KPI & ANALYTICS</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">Analyse de Performances</h1>
          <p className="text-zinc-400 mt-2">14 jours de data. Diagnostic IA en 1 clic.</p>
        </div>
        <select value={filterAcc} onChange={e => setFilterAcc(e.target.value)} data-testid="filter-account"
          className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white">
          <option value="all">Tous les comptes</option>
          {accounts.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
        </select>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          ["Reach total", filtered.reduce((s,k)=>s+k.reach,0).toLocaleString("fr-FR"), "#CCFF00"],
          ["Engagement moy.", `${filtered.length ? (filtered.reduce((s,k)=>s+k.engagement_rate,0)/filtered.length).toFixed(2) : 0}%`, "#00D4FF"],
          ["Watchtime moy.", `${filtered.length ? (filtered.reduce((s,k)=>s+k.watchtime_pct,0)/filtered.length).toFixed(1) : 0}%`, "#10B981"],
          ["Saves total", filtered.reduce((s,k)=>s+k.saves,0).toLocaleString("fr-FR"), "#F59E0B"],
        ].map(([l, v, c]) => (
          <div key={l} className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-5">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-2">{l}</div>
            <div className="font-mono text-3xl font-medium" style={{ color: c }}>{v}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChartCard title="Reach quotidien" data={series} dataKey="reach" color="#CCFF00" />
        <ChartCard title="Engagement %" data={series} dataKey="engagement_rate" color="#00D4FF" type="line" />
        <ChartCard title="Watchtime %" data={series} dataKey="watchtime_pct" color="#10B981" type="line" />
        <BarCard title="Saves / Shares / Comments" data={series} />
      </div>

      <div className="border border-[#00D4FF]/30 bg-[#00D4FF]/[0.02] rounded-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#00D4FF]">// DIAGNOSTIC IA</div>
            <h2 className="font-display text-xl font-semibold text-white mt-1">Analyse stratégique</h2>
          </div>
          <button onClick={analyze} disabled={loading} data-testid="analyze-btn"
            className="bg-[#00D4FF]/10 border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/20 px-4 py-2 rounded-sm text-sm font-mono uppercase tracking-wider flex items-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Analyser
          </button>
        </div>
        {analysis ? (
          <div className="text-sm text-zinc-200 whitespace-pre-line leading-relaxed border-t border-[#1E1E24] pt-4">{analysis}</div>
        ) : (
          <div className="text-sm text-zinc-500 italic border-t border-[#1E1E24] pt-4">Clique "Analyser" pour obtenir un diagnostic chirurgical par Claude Sonnet 4.5.</div>
        )}
      </div>
    </div>
  );
}

function ChartCard({ title, data, dataKey, color, type }) {
  return (
    <div className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-5">
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-3">{title}</div>
      <ResponsiveContainer width="100%" height={200}>
        {type === "line" ? (
          <LineChart data={data}>
            <CartesianGrid stroke="#1E1E24" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: "#52525B", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => v.slice(5)} />
            <YAxis tick={{ fill: "#52525B", fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: "#0C0C0E", border: "1px solid #1E1E24", borderRadius: 2, fontSize: 12 }} />
            <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={false} />
          </LineChart>
        ) : (
          <AreaChart data={data}>
            <defs>
              <linearGradient id={`grad-${dataKey}`} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.5} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#1E1E24" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: "#52525B", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => v.slice(5)} />
            <YAxis tick={{ fill: "#52525B", fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: "#0C0C0E", border: "1px solid #1E1E24", borderRadius: 2, fontSize: 12 }} />
            <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} fill={`url(#grad-${dataKey})`} />
          </AreaChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

function BarCard({ title, data }) {
  return (
    <div className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-5">
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-3">{title}</div>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data}>
          <CartesianGrid stroke="#1E1E24" vertical={false} />
          <XAxis dataKey="date" tick={{ fill: "#52525B", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => v.slice(5)} />
          <YAxis tick={{ fill: "#52525B", fontSize: 10 }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ background: "#0C0C0E", border: "1px solid #1E1E24", borderRadius: 2, fontSize: 12 }} />
          <Bar dataKey="saves" fill="#F59E0B" />
          <Bar dataKey="shares" fill="#00D4FF" />
          <Bar dataKey="comments" fill="#CCFF00" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
